import type { LineSeriesOption, YAXisComponentOption } from 'echarts';

import type { TimeSeries } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { lineVisuals } from '../config/chartVisuals';
import { getLineBucketCount } from './level-of-detail/getLineBucketCount';
import { getRenderableLineSeriesData } from './level-of-detail/lineSeriesDataCache';
import type { LineSeriesData } from './level-of-detail/lineSeriesDataCache';
import type { LineViewport } from './level-of-detail/lineViewport';

/** A point the line itself cannot show, because it has no neighbour to join (see buildLonePointsOption). */
export type LonePointMarker = {
  timestamp: number;
  /** Height within the plot, 0 at the bottom and 1 at the top, on the scale of its own line. */
  position: number;
  color: string | undefined;
  opacity: number;
};

export type LineSeriesOptions = {
  yAxis: YAXisComponentOption[];
  series: LineSeriesOption[];
  lonePoints: LonePointMarker[];
};

type BuildLineSeriesOptionParams = {
  series: TimeSeries[];
  /** Index of the first axis the lines may use; earlier indexes belong to the plot's own axes. */
  yAxisOffset: number;
  highlightedSeriesIds?: string[];
  /** Window and width the lines are thinned for (see LineViewport). */
  viewport: LineViewport;
  /** Lines allowed to show their symbols (see getSymbolSeriesIds). */
  symbolSeriesIds: ReadonlySet<string>;
  theme: TimeSeriesChartTheme;
};

type LineEmphasis = 'none' | 'highlighted' | 'dimmed';

const EMPTY_VALUE_RANGE = { min: 0, max: 1 };

function getLineEmphasis(seriesId: string, highlightedSeriesIds: Set<string>): LineEmphasis {
  if (highlightedSeriesIds.size === 0) {
    return 'none';
  }

  return highlightedSeriesIds.has(seriesId) ? 'highlighted' : 'dimmed';
}

/**
 * Each line scales to its own value range, so series with different units share the plot: the
 * series' minimum sits on the time axis and its maximum at the top of the plot, with no padding.
 */
function getValueAxisRange({ data, minValue, maxValue }: LineSeriesData) {
  if (data.length === 0 || !Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
    return EMPTY_VALUE_RANGE;
  }

  // A constant series would leave no range at all; it is drawn on the time axis.
  return maxValue > minValue ? { min: minValue, max: maxValue } : { min: minValue, max: minValue + 1 };
}

/** Placed by the same value range as the line's own axis, so a marker sits exactly where its point is. */
function getLonePointMarkers(
  lineSeriesData: LineSeriesData,
  color: string | undefined,
  emphasis: LineEmphasis
): LonePointMarker[] {
  const { min, max } = getValueAxisRange(lineSeriesData);
  const opacity = emphasis === 'dimmed' ? lineVisuals.dimmedOpacity : lineVisuals.defaultOpacity;

  return lineSeriesData.lonePoints.map(([timestamp, value]) => ({
    timestamp,
    position: (value - min) / (max - min),
    color,
    opacity,
  }));
}

function buildValueAxis(lineSeriesData: LineSeriesData): YAXisComponentOption {
  return {
    type: 'value',
    ...getValueAxisRange(lineSeriesData),
    show: false,
    scale: true,
  };
}

function buildLineStyle(color: string | undefined, emphasis: LineEmphasis): LineSeriesOption['lineStyle'] {
  switch (emphasis) {
    case 'highlighted':
      return {
        ...(color && { color }),
        width: lineVisuals.highlightedWidth,
        opacity: lineVisuals.defaultOpacity,
      };

    case 'dimmed':
      return {
        ...(color && { color }),
        width: lineVisuals.dimmedWidth,
        opacity: lineVisuals.dimmedOpacity,
      };

    case 'none':
      return {
        ...(color && { color }),
        width: lineVisuals.defaultWidth,
        opacity: lineVisuals.defaultOpacity,
      };

    default:
      return emphasis satisfies never;
  }
}

type BuildSeriesParams = {
  series: TimeSeries;
  lineSeriesData: LineSeriesData;
  yAxisIndex: number;
  emphasis: LineEmphasis;
  showSymbol: boolean;
  theme: TimeSeriesChartTheme;
};

function buildSeries({
  series,
  lineSeriesData,
  yAxisIndex,
  emphasis,
  showSymbol,
  theme,
}: BuildSeriesParams): LineSeriesOption {
  return {
    id: series.id,
    name: series.label,
    type: 'line',
    // ECharts 6 spelling; the older `triggerLineEvent` is deprecated there.
    triggerEvent: 'line',
    yAxisIndex,
    symbol: 'circle',
    showSymbol,
    symbolSize: lineVisuals.symbolSize,
    itemStyle: {
      color: series.color,
      borderColor: theme.symbol.borderColor,
      borderWidth: lineVisuals.symbolBorderWidth,
    },
    z: emphasis === 'highlighted' ? lineVisuals.highlightedZ : lineVisuals.z,
    data: lineSeriesData.data,
    emphasis: {
      disabled: true,
    },
    lineStyle: buildLineStyle(series.color, emphasis),
  };
}

/**
 * One ECharts line and one hidden value axis per neutral series, in series order, plus the lone
 * points of the lines that show no symbols of their own.
 */
export function buildLineSeriesOption({
  series,
  yAxisOffset,
  highlightedSeriesIds = [],
  viewport,
  symbolSeriesIds,
  theme,
}: BuildLineSeriesOptionParams): LineSeriesOptions {
  const highlightedSeriesIdSet = new Set(highlightedSeriesIds);
  const bucketCount = getLineBucketCount(viewport.plotWidthPx);
  const yAxis: YAXisComponentOption[] = [];
  const lines: LineSeriesOption[] = [];
  const lonePoints: LonePointMarker[] = [];

  series.forEach((timeSeries, index) => {
    const lineSeriesData = getRenderableLineSeriesData(timeSeries, viewport.visibleRange, bucketCount);
    const emphasis = getLineEmphasis(timeSeries.id, highlightedSeriesIdSet);
    // A dimmed line is context only; its symbols would compete with the highlighted line.
    const showSymbol = symbolSeriesIds.has(timeSeries.id) && emphasis !== 'dimmed';

    yAxis.push(buildValueAxis(lineSeriesData));
    lines.push(
      buildSeries({
        series: timeSeries,
        lineSeriesData,
        yAxisIndex: yAxisOffset + index,
        emphasis,
        showSymbol,
        theme,
      })
    );

    // With symbols on, a lone point already has its own circle.
    if (!showSymbol) {
      lonePoints.push(...getLonePointMarkers(lineSeriesData, timeSeries.color, emphasis));
    }
  });

  return { yAxis, series: lines, lonePoints };
}
